import React, { useMemo, useEffect, useRef } from 'react';
import moment from 'moment';

import { ReactComponent as DownSvg } from '@assets/icons/supplier/arrow-down.svg';
import { ReactComponent as UpSvg } from '@assets/icons/supplier/arrow-top.svg';

import CommonImage from '@/components/CommonImage';
import {
	PRIORITY_TAGS,
	PROJECT_TABLE_PRIORITY,
	PROJECT_TABLE_STATUS
} from '@/features/project-management-tools/constents';
import LoaderComponent from '@/components/LoaderComponent';
import { convertDateToLocal, replaceUnderLine, isPageReachBottom } from '@/utils/Util';
import { TABLE_COLUMNS } from '@/features/project-management-tools/constents';
import { useUpdateProjectMutation } from '@/features/project-management-tools/api/projectManagementApi';
import { toastError, toastSuccess } from '@/components/Toast';
import NoData from '@/components/NoData';
import { renameStatus } from '@/features/project-management-tools/utils';

// Sortable header component
const SortableHeader = ({ column, selectedSort, setSelectedSort, setIsDescending, isDescending }) => {
	const isActive = (sortValue) => sortValue === selectedSort;

	return (
		<th>
			<span className="w-100 d-flex align-items-center justify-content-between">
				{column.label}
				<span className="filter-icon d-flex flex-column justify-content-center arrow-icon cursor-pointer">
					<UpSvg
						className={isActive(`${column.sortKey}`) && isDescending ? 'active' : 'deactive'}
						onClick={() => {
							setSelectedSort(`${column.sortKey}`);
							setIsDescending(true);
						}}
					/>
					<DownSvg
						className={isActive(`${column.sortKey}`) && !isDescending ? 'active' : 'deactive'}
						onClick={() => {
							setSelectedSort(`${column.sortKey}`);
							setIsDescending(false);
						}}
					/>
				</span>
			</span>
		</th>
	);
};

// Table header component
const TableHeader = ({ selectedSort, setSelectedSort, setIsDescending, isDescending }) => (
	<thead>
		<tr>
			{TABLE_COLUMNS.map((column) => (
				<SortableHeader
					key={column.key}
					column={column}
					selectedSort={selectedSort}
					setSelectedSort={setSelectedSort}
					setIsDescending={setIsDescending}
					isDescending={isDescending}
				/>
			))}
		</tr>
	</thead>
);

// Progress bar component
const ProgressBar = ({ completed, total }) => {
	const percentage = total > 0 ? (completed / total) * 100 : 0;

	return (
		<div className="d-flex align-items-center justify-content-start mt-1">
			<div className="progress progress-square">
				<div className="progress-bar" style={{ width: `${percentage}%` }} />
			</div>
			<div className="progress-text ml-2">
				<p className="regular-14 gray_dark_01">
					{completed}/{total}
				</p>
			</div>
		</div>
	);
};

// Priority dropdown component
const PriorityDropdown = ({ priority, handleUpdatePriority, project }) => (
	<div className="option">
		<div className="dropdown">
			<button
				aria-expanded="false"
				className="btn btn-default dropdown-toggle"
				data-toggle="dropdown"
				id="menu1"
				type="button"
			>
				<span className={`mr-2 status ${PROJECT_TABLE_PRIORITY[priority]}`}>
					{priority || 'Select priority'}
				</span>
				<CommonImage
					className="arrow-icon"
					src="/icons/down-arrow-gray-dark-01.svg"
					width={20}
					height={20}
				/>
			</button>
			<ul
				aria-labelledby="menu1"
				className="dropdown-menu dropdown-menu-right shadow-lg"
				role="menu"
				x-placement="bottom-end"
			>
				{PRIORITY_TAGS?.map((tag) => (
					<li
						key={tag.constant}
						className="px-4 pb-3 pt-3"
						role="presentation"
						onClick={() =>
							handleUpdatePriority({
								...project,
								projectPriority: tag.constant
							})
						}
					>
						<span className={`font-weight-normal ${tag.className}`} role="menuitem" tabIndex={-1}>
							{tag.name}
						</span>
					</li>
				))}
			</ul>
		</div>
	</div>
);

// Table row component
const ProjectRow = ({ project, handleUpdatePriority }) => {
	const isDateExpired = useMemo(() => {
		const currentDate = moment().startOf('day');
		const endDate = moment(project?.endDate).startOf('day');
		return currentDate.isAfter(endDate);
	}, [project?.endDate]);

	const dateClassName = isDateExpired && project?.projectStatus !== 'COMPLETED' ? 'expired-date' : '';

	return (
		<tr key={project?.id}>
			<td className="brand-column">{project?.brandResponse?.name}</td>
			<td className="project-type-column">{replaceUnderLine(project?.ProjectType)}</td>
			<td className="project-name-column">
				<p
					className="cursor-pointer"
					onClick={() => {
						window.open(`/my-project/${project?.id}`, '_blank');
					}}
				>
					{project?.name}
				</p>
			</td>
			<td className="owner-column">
				<p>{project?.ownerResponse?.name}</p>
			</td>
			<td className={`start-date-column `}>
				<p>{convertDateToLocal(project?.startDate, 'DD-MMM, YYYY')}</p>
			</td>
			<td className={`end-date-column ${dateClassName}`}>
				<p>{convertDateToLocal(project?.endDate, 'DD-MMM, YYYY')}</p>
			</td>
			<td className={`buyer-date-column  ${dateClassName}`}>
				<p>{convertDateToLocal(project?.buyerFirstMeetingDate, 'DD-MMM, YYYY')}</p>
			</td>
			<td className="status-column">
				<span className={`status ${PROJECT_TABLE_STATUS[project?.projectStatus]}`}>
					{renameStatus(project?.projectStatus)}
				</span>
				<ProgressBar completed={project?.noOfCompletedTask} total={project?.noOfTotalTask} />
			</td>
			<td className="priority-column">
				<PriorityDropdown
					priority={project?.projectPriority}
					project={project}
					handleUpdatePriority={handleUpdatePriority}
				/>
			</td>
		</tr>
	);
};

// Main component
const ProjectTableView = ({
	projectListView,
	projectListViewLoading,
	setSelectedSort,
	selectedSort,
	refetchTasks,
	setIsDescending,
	isDescending,
	onLoadMore,
	hasMoreData,
	isLoadingMore
}) => {
	const [updateProject, { isLoading: isUpdating }] = useUpdateProjectMutation();
	const tableContainerRef = useRef(null);
	const isLoadingRef = useRef(false);

	const handleUpdatePriority = (project) => {
		updateProject({
			body: {
				projectPriority: project?.projectPriority
			},
			id: project?.id
		})
			.unwrap()
			.then(() => {
				toastSuccess('Project Updated Successfully!');
				refetchTasks();
			})
			.catch(({ error }) => {
				toastError(error?.data?.message || 'Something went wrong');
			});
	};

	const handleScroll = () => {
		if (isPageReachBottom(tableContainerRef.current) && !isLoadingRef.current && hasMoreData) {
			isLoadingRef.current = true;
			onLoadMore();
		}
	};

	useEffect(() => {
		isLoadingRef.current = isLoadingMore;
	}, [isLoadingMore]);

	useEffect(() => {
		const container = tableContainerRef.current;
		if (container) {
			container.addEventListener('scroll', handleScroll);
			return () => {
				container.removeEventListener('scroll', handleScroll);
			};
		}
	}, [hasMoreData, isLoadingMore]);

	return (
		<LoaderComponent loading={projectListViewLoading || isUpdating}>
			<div
				className={`project-list-view-table mt-3 
				${projectListView?.data?.length < 3 ? 'without-scroll-height' : ' '}`}
			>
				<div class="table-wrapper scroll-x-label scroll-y-label" ref={tableContainerRef}>
					<table className="table with-border height-sm">
						<TableHeader
							selectedSort={selectedSort}
							setSelectedSort={setSelectedSort}
							setIsDescending={setIsDescending}
							isDescending={isDescending}
						/>
						<tbody>
							{projectListView?.data?.map((project) => (
								<ProjectRow
									key={project?.id}
									project={project}
									handleUpdatePriority={handleUpdatePriority}
								/>
							))}

							{projectListView?.data?.length === 0 && (
								<tr>
									<td colSpan={TABLE_COLUMNS.length}>
										<div className="d-flex justify-content-center align-items-center w-100">
											<NoData message={'No project found'} />
										</div>
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</div>
		</LoaderComponent>
	);
};

export default ProjectTableView;
